import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useApiSend } from '../../../utils/httpClientTs';
import {
  createDelegate,
  updateDelegate,
  deleteDelegate,
  DelegateItem,
  DelegateReportingInfo,
  DelegateCreateRequest,
  DelegateUpdateRequest,
} from '../../../endpoints/delegates';
import UserSearchModal from '../../../Components/UserSearchModal';
import { config } from '../../../utils/config';

interface DelegatesSectionProps {
  chapterId: string;
  delegates: DelegateItem[];
  alternates: DelegateItem[];
  reporting?: DelegateReportingInfo;
  canEdit: boolean;
  onRefresh: () => void;
}

const DelegatesSection: React.FC<DelegatesSectionProps> = ({
  chapterId,
  delegates,
  alternates,
  reporting,
  canEdit,
  onRefresh,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState<DelegateItem | null>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    first_name?: string;
    last_name?: string;
  } | null>(null);
  const [delegateType, setDelegateType] = useState<'delegate' | 'alternate'>(
    'delegate'
  );
  const [order, setOrder] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // All hooks must be called before any conditional returns
  const { mutate: createDelegateMutate } = useApiSend(
    (data: DelegateCreateRequest) => createDelegate(chapterId, data),
    () => {
      setIsSaving(false);
      setShowAddModal(false);
      setShowUserSearch(false);
      setSelectedUser(null);
      setError(null);
      onRefresh();
    },
    (error: any) => {
      setError(error.message || 'Failed to create delegate');
      setIsSaving(false);
    }
  );

  const { mutate: updateDelegateMutate } = useApiSend(
    (data: DelegateUpdateRequest) =>
      updateDelegate(chapterId, selectedDelegate!.id, data),
    () => {
      setIsSaving(false);
      setShowEditModal(false);
      setSelectedDelegate(null);
      setError(null);
      onRefresh();
    },
    (error: any) => {
      setError(error.message || 'Failed to update delegate');
      setIsSaving(false);
    }
  );

  const { mutate: deleteDelegateMutate } = useApiSend(
    (delegateId: string) => deleteDelegate(chapterId, delegateId),
    () => {
      setIsSaving(false);
      setShowEditModal(false);
      setSelectedDelegate(null);
      setError(null);
      onRefresh();
    },
    (error: any) => {
      setError(error.message || 'Failed to delete delegate');
      setIsSaving(false);
    }
  );

  // Check if feature is enabled
  if (!config.ANNUAL_REPORTS_ENABLED) {
    return null;
  }

  // Don't show if no reporting record exists
  if (!reporting) {
    return null;
  }

  const handleAddClick = (type: 'delegate' | 'alternate') => {
    setDelegateType(type);
    setOrder(
      type === 'delegate' ? delegates.length + 1 : alternates.length + 1
    );
    setShowUserSearch(true);
  };

  const handleUserSelected = (user: any) => {
    setSelectedUser(user);
    setShowUserSearch(false);
    setShowAddModal(true);
  };

  const handleCreateDelegate = () => {
    if (!selectedUser) return;
    setIsSaving(true);
    setError(null);
    const request: DelegateCreateRequest = {
      user_id: selectedUser.id,
      delegate_type: delegateType,
      order: order,
    };
    createDelegateMutate(request);
  };

  const handleEditClick = (delegate: DelegateItem) => {
    setSelectedDelegate(delegate);
    setOrder(delegate.order);
    setShowEditModal(true);
  };

  const handleUpdateDelegate = () => {
    if (!selectedDelegate) return;
    setIsSaving(true);
    setError(null);
    const request: DelegateUpdateRequest = {
      order: order,
    };
    updateDelegateMutate(request);
  };

  const handleDeleteClick = (delegate: DelegateItem) => {
    if (
      window.confirm(`Are you sure you want to remove ${delegate.user_name}?`)
    ) {
      setSelectedDelegate(delegate);
      setIsSaving(true);
      setError(null);
      deleteDelegateMutate(delegate.id);
    }
  };

  return (
    <>
      <Grid item xs={12} sx={{ padding: 2 }}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title="S/P/D Delegates and Alternates"
            action={
              canEdit && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddClick('delegate')}
                    size="small"
                    disabled={
                      delegates.length >= (reporting?.number_of_delegates || 0)
                    }
                  >
                    Add Delegate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddClick('alternate')}
                    size="small"
                  >
                    Add Alternate
                  </Button>
                </Box>
              )
            }
          />
          <CardContent>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {reporting && (
              <Typography
                variant="body2"
                sx={{ mb: 2, color: 'text.secondary' }}
              >
                Quota: {delegates.length} / {reporting.number_of_delegates}{' '}
                delegates
              </Typography>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Delegates
                </Typography>
                {delegates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No delegates assigned
                  </Typography>
                ) : (
                  <Box>
                    {delegates
                      .sort((a, b) => a.order - b.order)
                      .map((delegate) => (
                        <Box
                          key={delegate.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body1">
                              {delegate.order}. {delegate.user_name}
                            </Typography>
                          </Box>
                          {canEdit && (
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(delegate)}
                                disabled={isSaving}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(delegate)}
                                disabled={isSaving}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Alternates
                </Typography>
                {alternates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No alternates assigned
                  </Typography>
                ) : (
                  <Box>
                    {alternates
                      .sort((a, b) => a.order - b.order)
                      .map((alternate) => (
                        <Box
                          key={alternate.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body1">
                              Alt {alternate.order}. {alternate.user_name}
                            </Typography>
                          </Box>
                          {canEdit && (
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(alternate)}
                                disabled={isSaving}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(alternate)}
                                disabled={isSaving}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* User Search Modal */}
      <UserSearchModal
        modalOpen={showUserSearch}
        setModalOpen={(open) => {
          if (!open) {
            setSelectedUser(null);
          }
          setShowUserSearch(open);
        }}
        userObjectRet={true}
        setUser={(user) => {
          handleUserSelected(user);
        }}
        chapter={chapterId}
        activeMembers={true}
        statusFilter="active"
      />

      {/* Add Delegate Modal */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add {delegateType === 'delegate' ? 'Delegate' : 'Alternate'}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedUser.first_name} {selectedUser.last_name}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
            margin="normal"
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateDelegate}
            variant="contained"
            disabled={isSaving || !selectedUser}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Delegate Modal */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit{' '}
          {selectedDelegate?.delegate_type === 'delegate'
            ? 'Delegate'
            : 'Alternate'}
        </DialogTitle>
        <DialogContent>
          {selectedDelegate && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">
                {selectedDelegate.user_name}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
            margin="normal"
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateDelegate}
            variant="contained"
            disabled={isSaving}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DelegatesSection;
